import { Injectable, NgZone, inject } from '@angular/core';
import { API_CONFIG, type ApiConfig } from '../api-config';
import { AuthService } from '../auth.service';

const RETRY_MS = 2000;

/**
 * Una conexión SSE compartida por el header, el hilo y la lista de soporte.
 */
@Injectable({ providedIn: 'root' })
export class StaffInboxStream {
  private readonly auth = inject(AuthService);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);
  private readonly zone = inject(NgZone);
  private readonly listeners = new Set<(event: string) => void>();
  private abort: AbortController | null = null;
  private running = false;

  listen(onEvent: (event: string) => void): () => void {
    this.listeners.add(onEvent);
    this.ensure();
    return () => {
      this.listeners.delete(onEvent);
      if (this.listeners.size === 0) this.stop();
    };
  }

  private ensure(): void {
    if (this.running) return;
    this.running = true;
    void this.connect();
  }

  private stop(): void {
    this.running = false;
    this.abort?.abort();
    this.abort = null;
  }

  private async connect(): Promise<void> {
    while (this.running) {
      const token = this.auth.getAccessToken();
      if (!token) {
        await wait(RETRY_MS);
        continue;
      }
      this.abort = new AbortController();
      try {
        const response = await fetch(`${this.apiConfig.apiUrl}/inbox/stream`, {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'text/event-stream',
          },
          signal: this.abort.signal,
        });
        if (!response.ok || !response.body) {
          await wait(response.status === 401 ? 15000 : RETRY_MS);
          continue;
        }
        await this.read(response);
      } catch {
        if (!this.running) return;
      }
      if (this.running) await wait(RETRY_MS);
    }
  }

  private async read(response: Response): Promise<void> {
    const reader = response.body?.getReader();
    if (!reader) return;
    const decoder = new TextDecoder();
    let buffer = '';
    while (this.running) {
      const chunk = await reader.read();
      if (chunk.done) return;
      buffer += decoder.decode(chunk.value, { stream: true });
      const frames = buffer.split('\n\n');
      buffer = frames.pop() ?? '';
      for (const frame of frames) {
        const line = frame.split('\n').find((row) => row.startsWith('event:'));
        if (!line) continue;
        const event = line.slice(6).trim();
        if (event === 'ready' || event === 'ping') continue;
        this.zone.run(() => this.listeners.forEach((listener) => listener(event)));
      }
    }
  }
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
